// src/pages/admin/AdminEditListing.js
// Formulaire de modification avec champs spécifiques par catégorie - NE PAS MODIFIER

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { CATEGORIES } from '../../data/categories';
import { LISTING_STATUS_LABELS } from '../../constants/listingStatus';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import apiClient from '../../api/client';

const NEW_TYPE_VALUE = '__new__';

const AdminEditListing = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState('locations');
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [form, setForm] = useState({
    title: '', type: '', quartier: '', city: 'Douala', price: '',
    bedrooms: '', bathrooms: '', area: '', capacity: '', floor: '',
    legalStatus: '', viabilise: false, cloture: false, proLabel: '', coveredAreas: '',
    description: '', descriptionEn: '', lat: '', lng: '',
    ownerName: '', ownerPhone: '', contactPhone: '',
    amenities: '', status: 'published',
    isSponsored: false,
  });

  const [quartierSuggestions, setQuartierSuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [typesByCategory, setTypesByCategory] = useState({});
  const [selectedTypeOption, setSelectedTypeOption] = useState('');
  const [customType, setCustomType] = useState('');

  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState([]);
  const [searchingPlace, setSearchingPlace] = useState(false);

  useEffect(() => {
    const loadListing = async () => {
      setLoading(true);
      try {
        const result = await apiClient.get(`/endpoints/listings.php?action=detail&id=${id}`);
        setCategory(result.category || 'locations');
        setForm({
          title: result.title || '',
          type: result.type_key || '',
          quartier: result.quartier || '',
          city: result.city || 'Douala',
          price: String(result.price ?? ''),
          bedrooms: result.bedrooms ?? '',
          bathrooms: result.bathrooms ?? '',
          area: result.area ?? '',
          capacity: result.capacity ?? '',
          floor: result.floor ?? '',
          legalStatus: result.legal_status ?? '',
          viabilise: !!result.viabilise,
          cloture: !!result.cloture,
          proLabel: result.pro_label ?? '',
          coveredAreas: result.covered_areas ?? '',
          description: result.description || '',
          descriptionEn: result.description_en || '',
          lat: result.lat ?? '',
          lng: result.lng ?? '',
          ownerName: result.ownerName || '',
          ownerPhone: result.ownerPhone || '',
          contactPhone: result.contact_phone || '',
          amenities: Array.isArray(result.amenities) ? result.amenities.join(', ') : '',
          status: result.status || 'published',
          isSponsored: !!result.is_sponsored,
        });
        setSelectedTypeOption(result.type_key || '');
      } catch (err) {
        alert("Impossible de charger l'annonce");
      } finally {
        setLoading(false);
      }
    };
    loadListing();
  }, [id]);

  useEffect(() => {
    const loadSuggestions = async () => {
      try {
        const [quartiersRes, typesRes] = await Promise.all([
          apiClient.get('/endpoints/quartiers.php?action=list'),
          apiClient.get('/endpoints/types.php?action=list'),
        ]);
        const quartiers = quartiersRes.quartiers || [];
        setQuartierSuggestions(quartiers.map((q) => q.name));
        setCitySuggestions([...new Set(quartiers.map((q) => q.city))]);
        setTypesByCategory(typesRes.types || {});
      } catch (err) {
        // pas grave
      }
    };
    loadSuggestions();
  }, []);

  const currentTypeOptions = typesByCategory[category] || [];

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Change de catégorie SANS effacer les champs déjà remplis - juste change quels champs sont visibles
  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    setSelectedTypeOption('');
    setCustomType('');
    handleChange('type', '');
  };

  const handleTypeSelect = (value) => {
    setSelectedTypeOption(value);
    if (value === NEW_TYPE_VALUE) {
      handleChange('type', '');
    } else {
      handleChange('type', value);
      setCustomType('');
    }
  };

  const handleCustomTypeChange = (value) => {
    setCustomType(value);
    handleChange('type', value);
  };

  const handleNewImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const mapped = files.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setNewImages((prev) => [...prev, ...mapped]);
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingImage = async (imageId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await apiClient.delete(`/endpoints/upload.php?action=delete-image&id=${imageId}`);
      setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      alert('Erreur lors de la suppression de la photo');
    }
  };

  const searchPlace = useCallback((query) => {
    setPlaceQuery(query);
    if (query.trim().length < 3) {
      setPlaceResults([]);
      return;
    }
    setSearchingPlace(true);
    apiClient
      .get(`/endpoints/geocode.php?action=search&q=${encodeURIComponent(query)}`)
      .then((res) => setPlaceResults(res.results || []))
      .catch(() => setPlaceResults([]))
      .finally(() => setSearchingPlace(false));
  }, []);

  const handleSelectPlace = (place) => {
    setForm((prev) => ({
      ...prev,
      quartier: place.name.split(',')[0].trim(),
      city: place.city || prev.city,
      lat: place.lat ?? prev.lat,
      lng: place.lng ?? prev.lng,
    }));
    setPlaceQuery('');
    setPlaceResults([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const listingData = {
        ...form,
        category,
        amenities: form.amenities ? form.amenities.split(',').map((a) => a.trim()) : [],
      };

      await apiClient.put(`/endpoints/listings.php?action=update&id=${id}`, listingData);

      for (const img of newImages) {
        const formData = new FormData();
        formData.append('listingId', id);
        formData.append('image', img.file);
        try {
          await apiClient.uploadFile('/endpoints/upload.php?action=image', formData);
        } catch (uploadErr) {
          console.error('Erreur upload image:', uploadErr);
        }
      }

      navigate(ROUTES.ADMIN_LISTINGS);
    } catch (err) {
      alert(err.message || "Une erreur serveur est survenue, réessaie.");
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A', paddingBottom: '80px' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF' },
    categoryWarning: { margin: '12px 20px 0', padding: '10px 14px', backgroundColor: 'rgba(66, 153, 225, 0.1)', border: '1px solid rgba(66, 153, 225, 0.3)', borderRadius: '8px', color: '#4299E1', fontSize: '12px' },
    form: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
    categoryTabs: { display: 'flex', gap: '8px', overflowX: 'auto', scrollbarWidth: 'none' },
    catTab: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', borderRadius: '10px', backgroundColor: '#162033', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#A0AEC0', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    catTabActive: { backgroundColor: 'rgba(255, 215, 0, 0.15)', border: '1px solid rgba(255, 215, 0, 0.3)', color: '#FFD700' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    row: { display: 'flex', gap: '12px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#A0AEC0' },
    optional: { color: '#4A5568', fontWeight: '400', fontSize: '11px' },
    hint: { fontSize: '11px', color: '#4A5568' },
    input: { padding: '12px 14px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '15px', outline: 'none', width: '100%' },
    select: { padding: '12px 14px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '15px', outline: 'none', width: '100%' },
    textarea: { padding: '12px 14px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '15px', outline: 'none', minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' },
    checkbox: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#FFFFFF', cursor: 'pointer' },
    sectionLabel: { fontSize: '14px', fontWeight: '700', color: '#FFD700', marginTop: '8px' },
    sponsoredBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', backgroundColor: 'rgba(255, 215, 0, 0.08)', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '10px', cursor: 'pointer' },
    sponsoredIcon: { fontSize: '20px' },
    sponsoredTextGroup: { display: 'flex', flexDirection: 'column', gap: '2px' },
    sponsoredLabel: { fontSize: '14px', fontWeight: '700', color: '#FFD700' },
    sponsoredDesc: { fontSize: '12px', color: '#A0AEC0' },
    placeSearchBox: { padding: '14px', backgroundColor: 'rgba(66, 153, 225, 0.08)', border: '1px solid rgba(66, 153, 225, 0.3)', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '8px' },
    placeResults: { display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' },
    placeResultItem: { padding: '10px 12px', backgroundColor: '#111827', borderRadius: '8px', fontSize: '13px', color: '#FFFFFF', cursor: 'pointer', border: '1px solid rgba(255, 255, 255, 0.05)' },
    contactPublicBox: { padding: '14px', backgroundColor: 'rgba(72, 187, 120, 0.08)', border: '1px solid rgba(72, 187, 120, 0.3)', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px' },
    contactPublicLabel: { fontSize: '13px', fontWeight: '700', color: '#48BB78' },
    contactPublicDesc: { fontSize: '12px', color: '#A0AEC0', lineHeight: '1.4' },
    contactPrivateBox: { padding: '14px', backgroundColor: 'rgba(245, 101, 101, 0.06)', border: '1px solid rgba(245, 101, 101, 0.25)', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px' },
    contactPrivateLabel: { fontSize: '13px', fontWeight: '700', color: '#F56565' },
    contactPrivateDesc: { fontSize: '12px', color: '#A0AEC0', lineHeight: '1.4' },
    imageGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
    imageBox: { position: 'relative', width: '80px', height: '80px' },
    imageThumb: { width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover' },
    removeImgBtn: { position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#F56565', color: '#FFFFFF', border: 'none', fontSize: '12px', cursor: 'pointer' },
    uploadBtn: { width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed rgba(255, 215, 0, 0.3)', backgroundColor: '#111827', color: '#FFD700', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    bottomBar: { position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '900px', display: 'flex', gap: '10px', padding: '12px 20px', backgroundColor: '#0A0E1A', borderTop: '1px solid rgba(255, 255, 255, 0.1)' },
    saveBtn: { flex: 1, padding: '14px', backgroundColor: '#FFD700', border: 'none', borderRadius: '10px', color: '#0A0E1A', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Chargement de l'annonce..." />;
  }

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Modifier l'annonce | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_LISTINGS)}>←</button>
        <div style={styles.title}>Modifier #{id}</div>
      </div>

      <div style={styles.categoryWarning}>
        ℹ️ Les champs ci-dessous changent selon la catégorie sélectionnée. Terrain ≠ Appartement ≠ Service.
      </div>

      <div style={styles.form}>
        {/* Catégorie */}
        <div style={styles.field}>
          <label style={styles.label}>Catégorie</label>
          <div style={styles.categoryTabs} className="hide-scrollbar">
            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
            {CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                style={{ ...styles.catTab, ...(category === cat.id ? styles.catTabActive : {}) }}
                onClick={() => handleCategoryChange(cat.id)}
              >
                <span>{cat.icon}</span>
                {cat.id === 'immobilier' ? 'Parcelles' : cat.id}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Titre <span style={styles.optional}>(facultatif)</span></label>
          <input type="text" style={styles.input} value={form.title} onChange={(e) => handleChange('title', e.target.value)} />
        </div>

        <div style={styles.placeSearchBox}>
          <label style={styles.label}>🔍 Rechercher un lieu (optionnel, remplit automatiquement)</label>
          <input
            type="text"
            style={styles.input}
            value={placeQuery}
            onChange={(e) => searchPlace(e.target.value)}
            placeholder="Ex: Ndogbong, Douala..."
          />
          {searchingPlace && <span style={styles.hint}>Recherche...</span>}
          {placeResults.length > 0 && (
            <div style={styles.placeResults}>
              {placeResults.map((place, index) => (
                <div key={index} style={styles.placeResultItem} onClick={() => handleSelectPlace(place)}>
                  📍 {place.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Type <span style={styles.optional}>(facultatif)</span></label>
          <select style={styles.select} value={selectedTypeOption} onChange={(e) => handleTypeSelect(e.target.value)}>
            <option value="">Choisir un type existant</option>
            {currentTypeOptions.map((t) => (
              <option key={t.rowId || t.id} value={t.name}>{t.name}</option>
            ))}
            <option value={NEW_TYPE_VALUE}>➕ Ajouter un nouveau type...</option>
          </select>
          {selectedTypeOption === NEW_TYPE_VALUE && (
            <input
              type="text"
              style={styles.input}
              value={customType}
              onChange={(e) => handleCustomTypeChange(e.target.value)}
              placeholder="Nom du nouveau type (ex: Bureau, Duplex...)"
              autoFocus
            />
          )}
        </div>

        <div style={styles.row}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Ville <span style={styles.optional}>(facultatif)</span></label>
            <input
              type="text"
              style={styles.input}
              list="edit-city-suggestions"
              value={form.city}
              onChange={(e) => handleChange('city', e.target.value)}
            />
            <datalist id="edit-city-suggestions">
              {citySuggestions.map((c, i) => (<option key={i} value={c} />))}
            </datalist>
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Quartier <span style={styles.optional}>(facultatif)</span></label>
            <input
              type="text"
              style={styles.input}
              list="edit-quartier-suggestions"
              value={form.quartier}
              onChange={(e) => handleChange('quartier', e.target.value)}
            />
            <datalist id="edit-quartier-suggestions">
              {quartierSuggestions.map((q, i) => (<option key={i} value={q} />))}
            </datalist>
            <span style={styles.hint}>Pas trouvé ? Tape simplement le nom, la carte le trouvera quand même.</span>
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>
            Prix (FCFA) <span style={styles.optional}>(facultatif)</span>
            {category === 'sejours' && ' / nuit'}
            {category === 'locations' && ' / mois'}
            {category === 'immobilier' && ' / m²'}
          </label>
          <input type="number" style={styles.input} value={form.price} onChange={(e) => handleChange('price', e.target.value)} />
        </div>

        {/* Champs spécifiques SÉJOURS / LOCATIONS */}
        {(category === 'sejours' || category === 'locations') && (
          <>
            <div style={styles.sectionLabel}>Détails du logement</div>
            <div style={styles.row}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Chambres</label>
                <input type="number" style={styles.input} value={form.bedrooms} onChange={(e) => handleChange('bedrooms', e.target.value)} placeholder="2" />
              </div>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Salles de bain</label>
                <input type="number" style={styles.input} value={form.bathrooms} onChange={(e) => handleChange('bathrooms', e.target.value)} placeholder="1" />
              </div>
            </div>
            <div style={styles.row}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Superficie (m²)</label>
                <input type="number" style={styles.input} value={form.area} onChange={(e) => handleChange('area', e.target.value)} placeholder="85" />
              </div>
              {category === 'sejours' && (
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Capacité (pers.)</label>
                  <input type="number" style={styles.input} value={form.capacity} onChange={(e) => handleChange('capacity', e.target.value)} placeholder="4" />
                </div>
              )}
              {category === 'locations' && (
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Étage</label>
                  <input type="text" style={styles.input} value={form.floor} onChange={(e) => handleChange('floor', e.target.value)} placeholder="RDC, 1er..." />
                </div>
              )}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Équipements (séparés par virgule)</label>
              <input type="text" style={styles.input} value={form.amenities} onChange={(e) => handleChange('amenities', e.target.value)} placeholder="Wifi, Climatisation, Parking" />
            </div>
          </>
        )}

        {/* Champs spécifiques IMMOBILIER / PARCELLES */}
        {category === 'immobilier' && (
          <>
            <div style={styles.sectionLabel}>Détails de la parcelle</div>
            <div style={styles.field}>
              <label style={styles.label}>Superficie (m²)</label>
              <input type="number" style={styles.input} value={form.area} onChange={(e) => handleChange('area', e.target.value)} placeholder="500" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Statut juridique</label>
              <input type="text" style={styles.input} value={form.legalStatus} onChange={(e) => handleChange('legalStatus', e.target.value)} placeholder="Titre foncier, ..." />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Label professionnel</label>
              <input type="text" style={styles.input} value={form.proLabel} onChange={(e) => handleChange('proLabel', e.target.value)} placeholder="Parcelle viabilisée" />
            </div>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={form.viabilise} onChange={(e) => handleChange('viabilise', e.target.checked)} />
              Viabilisé
            </label>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={form.cloture} onChange={(e) => handleChange('cloture', e.target.checked)} />
              Clôturé
            </label>
          </>
        )}

        {/* Champs spécifiques SERVICES */}
        {category === 'services' && (
          <>
            <div style={styles.sectionLabel}>Détails du service</div>
            <div style={styles.field}>
              <label style={styles.label}>Quartiers couverts</label>
              <input type="text" style={styles.input} value={form.coveredAreas} onChange={(e) => handleChange('coveredAreas', e.target.value)} placeholder="Bonapriso, Akwa, Bonanjo..." />
            </div>
          </>
        )}

        <div style={styles.sectionLabel}>Mise en avant</div>
        <label style={styles.sponsoredBox}>
          <input type="checkbox" checked={form.isSponsored} onChange={(e) => handleChange('isSponsored', e.target.checked)} />
          <span style={styles.sponsoredIcon}>⭐</span>
          <div style={styles.sponsoredTextGroup}>
            <span style={styles.sponsoredLabel}>Annonce sponsorisée / premium</span>
            <span style={styles.sponsoredDesc}>Apparaîtra en priorité dans une section spéciale sur l'accueil</span>
          </div>
        </label>

        <div style={styles.sectionLabel}>Photos</div>
        <div style={styles.imageGrid}>
          {existingImages.map((img) => (
            <div key={img.id || img} style={styles.imageBox}>
              <img src={typeof img === 'string' ? img : img.image_path} alt="" style={styles.imageThumb} />
              {img.id && (
                <button style={styles.removeImgBtn} onClick={() => handleDeleteExistingImage(img.id)}>✕</button>
              )}
            </div>
          ))}
          {newImages.map((img, index) => (
            <div key={`new-${index}`} style={styles.imageBox}>
              <img src={img.preview} alt="" style={styles.imageThumb} />
              <button style={styles.removeImgBtn} onClick={() => removeNewImage(index)}>✕</button>
            </div>
          ))}
          <label style={styles.uploadBtn}>
            +
            <input type="file" accept="image/*" multiple onChange={handleNewImageUpload} style={{ display: 'none' }} />
          </label>
        </div>

        <div style={styles.sectionLabel}>Descriptions</div>
        <div style={styles.field}>
          <label style={styles.label}>Description (Français)</label>
          <textarea style={styles.textarea} value={form.description} onChange={(e) => handleChange('description', e.target.value)} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Description (English)</label>
          <textarea style={styles.textarea} value={form.descriptionEn} onChange={(e) => handleChange('descriptionEn', e.target.value)} />
        </div>

        <div style={styles.sectionLabel}>Localisation GPS</div>
        <div style={styles.row}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Latitude</label>
            <input type="text" style={styles.input} value={form.lat} onChange={(e) => handleChange('lat', e.target.value)} />
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Longitude</label>
            <input type="text" style={styles.input} value={form.lng} onChange={(e) => handleChange('lng', e.target.value)} />
          </div>
        </div>

        <div style={styles.sectionLabel}>Contact affiché aux visiteurs</div>
        <div style={styles.contactPublicBox}>
          <span style={styles.contactPublicLabel}>💬 Numéro de contact public</span>
          <span style={styles.contactPublicDesc}>C'est CE numéro que les visiteurs contacteront en cliquant "Contacter" sur l'annonce.</span>
          <input
            type="tel"
            style={styles.input}
            value={form.contactPhone}
            onChange={(e) => handleChange('contactPhone', e.target.value)}
            placeholder="+237 6XX XXX XXX"
          />
        </div>

        <div style={styles.sectionLabel}>Contact propriétaire (privé)</div>
        <div style={styles.contactPrivateBox}>
          <span style={styles.contactPrivateLabel}>🔒 Réservé à l'administration</span>
          <span style={styles.contactPrivateDesc}>Jamais visible par les visiteurs. Sert uniquement à toi pour joindre le propriétaire.</span>
          <input
            type="text"
            style={styles.input}
            value={form.ownerName}
            onChange={(e) => handleChange('ownerName', e.target.value)}
            placeholder="Nom du propriétaire"
          />
          <input
            type="tel"
            style={styles.input}
            value={form.ownerPhone}
            onChange={(e) => handleChange('ownerPhone', e.target.value)}
            onBlur={(e) => handleChange('ownerPhone', e.target.value)}
            placeholder="+237 6XX XXX XXX"
            autoComplete="tel"
          />
        </div>

        <div style={styles.sectionLabel}>Statut</div>
        <div style={styles.field}>
          <select style={styles.select} value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
            {Object.entries(LISTING_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.bottomBar}>
        <button style={{ ...styles.saveBtn, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
};

export default AdminEditListing;